/* Ship It — theme.js
   Vanilla custom elements, no dependencies. Loaded with `defer`. */
(function () {
  'use strict';

  var routes = window.routes || {};
  var strings = window.themeStrings || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Helpers ------------------------------------------------------------ */

  function announce(message) {
    var region = document.querySelector('[data-live-region]');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 50);
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function setPrice(scope, text, compare) {
    scope.querySelectorAll('[data-price]').forEach(function (el) {
      el.textContent = '';
      var current = document.createElement('span');
      current.className = 'price__current';
      current.textContent = text;
      el.appendChild(current);
      el.classList.toggle('price--sale', !!compare);
      if (compare) {
        var was = document.createElement('s');
        was.className = 'price__compare';
        was.textContent = compare;
        el.appendChild(was);
      }
    });
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      if (count > 0) el.removeAttribute('data-empty');
      else el.setAttribute('data-empty', 'true');
    });
  }

  function countFrom(doc) {
    var source = doc.querySelector('[data-cart-count-source]');
    return source ? parseInt(source.getAttribute('data-cart-count-source'), 10) || 0 : null;
  }

  function sectionsParam(ids) {
    return ids.join(',');
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok || json.status) {
          var err = new Error(json.description || json.message || strings.cartError);
          err.payload = json;
          throw err;
        }
        return json;
      });
    });
  }

  function changeLine(line, quantity, sectionIds) {
    return post(routes.cart_change_url + '.js', {
      line: line,
      quantity: quantity,
      sections: sectionsParam(sectionIds),
      sections_url: window.location.pathname
    });
  }

  /* Native <dialog> drawers -------------------------------------------- */

  var drawers = {
    open: function (dialog, trigger) {
      if (!dialog || dialog.open) return;
      dialog._returnFocus = trigger || document.activeElement;
      dialog.showModal();
      document.body.classList.add('is-locked');
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          dialog.classList.add('is-open');
        });
      });
      var focusTarget = dialog.querySelector('[data-drawer-close]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    },
    close: function (dialog) {
      if (!dialog || !dialog.open || dialog._closing) return;
      dialog._closing = true;
      dialog.classList.remove('is-open');
      var panel = dialog.querySelector('.drawer__panel');
      var finish = function () {
        dialog._closing = false;
        if (dialog.open) dialog.close();
      };
      if (reduceMotion.matches || !panel) {
        finish();
        return;
      }
      var done = false;
      var once = function () {
        if (done) return;
        done = true;
        panel.removeEventListener('transitionend', once);
        finish();
      };
      panel.addEventListener('transitionend', once);
      window.setTimeout(once, 400);
    }
  };

  function wireDialog(dialog) {
    if (dialog._wired) return;
    dialog._wired = true;
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      drawers.close(dialog);
    });
    dialog.addEventListener('close', function () {
      dialog.classList.remove('is-open');
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('is-locked');
      var back = dialog._returnFocus;
      if (back && document.contains(back) && typeof back.focus === 'function') {
        back.focus({ preventScroll: true });
      }
    });
    dialog.addEventListener('click', function (event) {
      // The dialog box is the full-viewport scrim; the panel is a child.
      if (event.target === dialog) drawers.close(dialog);
    });
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      var target = document.querySelector(opener.getAttribute('data-drawer-open'));
      if (target) {
        event.preventDefault();
        wireDialog(target);
        drawers.open(target, opener);
      }
      return;
    }
    var closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      drawers.close(closer.closest('dialog'));
    }
  });

  /* <cart-drawer> ------------------------------------------------------- */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      wireDialog(this.dialog);
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      document.addEventListener('click', this.onTrigger.bind(this));
    }

    onTrigger(event) {
      var trigger = event.target.closest('[data-cart-trigger]');
      if (!trigger || window.themeSettings.cartType !== 'drawer') return;
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      this.open(trigger);
    }

    open(trigger) {
      drawers.open(this.dialog, trigger);
    }

    close() {
      drawers.close(this.dialog);
    }

    onClick(event) {
      var remove = event.target.closest('[data-remove]');
      if (!remove) return;
      event.preventDefault();
      this.update(parseInt(remove.getAttribute('data-line'), 10), 0, remove);
    }

    onChange(event) {
      var input = event.target.closest('input[data-line]');
      if (!input) return;
      this.update(parseInt(input.getAttribute('data-line'), 10), parseInt(input.value, 10) || 0, input);
    }

    update(line, quantity, origin) {
      var self = this;
      var row = origin.closest('.line');
      var key = row ? row.getAttribute('data-line-key') : null;
      if (row) row.classList.add('is-updating');
      changeLine(line, quantity, ['cart-drawer'])
        .then(function (cart) {
          self.render(cart.sections && cart.sections['cart-drawer']);
          self.restoreFocus(key);
          announce(strings.cartUpdated);
        })
        .catch(function (err) {
          announce(err.message);
          self.reload();
        });
    }

    reload() {
      var self = this;
      fetch(routes.root_url + '?sections=cart-drawer')
        .then(function (res) {
          return res.json();
        })
        .then(function (sections) {
          self.render(sections['cart-drawer']);
        });
    }

    render(html) {
      if (!html) return;
      var doc = parseHTML(html);
      var next = doc.querySelector('[data-cart-drawer-inner]');
      var inner = this.querySelector('[data-cart-drawer-inner]');
      if (next && inner) inner.innerHTML = next.innerHTML;
      var count = countFrom(doc);
      if (count !== null) updateCartCount(count);
    }

    restoreFocus(key) {
      var target = key && this.querySelector('[data-line-key="' + key + '"] .qty__input');
      if (!target) target = this.querySelector('[data-drawer-close]');
      if (target) target.focus({ preventScroll: true });
    }

    status(text) {
      var bar = this.querySelector('[data-cart-status]');
      var label = this.querySelector('[data-cart-status-text]');
      if (!bar || !label) return;
      label.textContent = text;
      bar.hidden = false;
    }
  }

  /* <cart-page> --------------------------------------------------------- */

  class CartPage extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
    }

    sectionIds() {
      var ids = [this.getAttribute('data-section-id')];
      if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
      return ids;
    }

    onClick(event) {
      var remove = event.target.closest('[data-remove]');
      if (!remove) return;
      event.preventDefault();
      this.update(parseInt(remove.getAttribute('data-line'), 10), 0, remove);
    }

    onChange(event) {
      var input = event.target.closest('input[data-line]');
      if (!input) return;
      this.update(parseInt(input.getAttribute('data-line'), 10), parseInt(input.value, 10) || 0, input);
    }

    update(line, quantity, origin) {
      var self = this;
      var row = origin.closest('.line');
      var key = row ? row.getAttribute('data-line-key') : null;
      if (row) row.classList.add('is-updating');
      changeLine(line, quantity, this.sectionIds())
        .then(function (cart) {
          self.render(cart.sections);
          var target = key && self.querySelector('[data-line-key="' + key + '"] .qty__input');
          if (target) target.focus({ preventScroll: true });
          announce(strings.cartUpdated);
        })
        .catch(function (err) {
          announce(err.message);
          window.location.reload();
        });
    }

    render(sections) {
      if (!sections) return;
      var id = this.getAttribute('data-section-id');
      if (sections[id]) {
        var doc = parseHTML(sections[id]);
        var next = doc.querySelector('cart-page');
        if (next) this.innerHTML = next.innerHTML;
        var count = countFrom(doc);
        if (count !== null) updateCartCount(count);
      }
      var drawer = document.querySelector('cart-drawer');
      if (drawer && sections['cart-drawer']) drawer.render(sections['cart-drawer']);
    }
  }

  /* <product-form> ------------------------------------------------------ */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-button]');
      this.error = this.querySelector('[data-error]');
      this.select = this.querySelector('[data-variant-select]');
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
      if (this.select) this.select.addEventListener('change', this.onSelect.bind(this));
    }

    // Compact variant select (hero and spec sheet): keep the price in step with the chosen variant.
    onSelect() {
      var option = this.select.options[this.select.selectedIndex];
      if (!option) return;
      var scope = this.closest('[data-product-scope]') || document;
      setPrice(scope, option.getAttribute('data-price'), null);
      var ok = option.getAttribute('data-available') !== 'false';
      this.button.disabled = !ok;
      this.button.classList.remove('is-added');
      var label = this.button.querySelector('[data-add-label]');
      if (label) label.textContent = ok ? this.addLabel() : strings.soldOut;
    }

    addLabel() {
      if (this.defaultLabel === undefined) {
        var label = this.button.querySelector('[data-add-label]');
        this.defaultLabel = label ? label.textContent.trim() : strings.addToCart;
        if (!this.defaultLabel || this.defaultLabel === strings.soldOut) this.defaultLabel = strings.addToCart;
      }
      return this.defaultLabel;
    }

    onSubmit(event) {
      var self = this;
      var drawer = document.querySelector('cart-drawer');
      var useDrawer = drawer && window.themeSettings.cartType === 'drawer';
      var idInput = this.form.querySelector('[name="id"]');
      if (!idInput || idInput.disabled || !this.button || this.button.disabled) return;
      // Only intercept the add button; dynamic checkout buttons submit normally.
      if (event.submitter && event.submitter !== this.button) return;
      event.preventDefault();

      this.hideError();
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-disabled', 'true');

      var body = new FormData(this.form);
      if (useDrawer) body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);

      fetch(routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body
      })
        .then(function (res) {
          return res.json().then(function (json) {
            if (!res.ok || json.status) {
              throw new Error(json.description || json.message || strings.cartError);
            }
            return json;
          });
        })
        .then(function (item) {
          if (!useDrawer) {
            window.location.href = routes.cart_url;
            return;
          }
          drawer.render(item.sections && item.sections['cart-drawer']);
          var name = item.product_title + (item.variant_title ? ' \u00b7 ' + item.variant_title : '');
          drawer.status(strings.addedItem.replace('[[title]]', name));
          announce(strings.addedItem.replace('[[title]]', name));
          self.flipButton();
          drawer.open(self.button);
        })
        .catch(function (err) {
          self.showError(err.message);
        })
        .then(function () {
          self.button.classList.remove('is-loading');
          self.button.removeAttribute('aria-disabled');
        });
    }

    flipButton() {
      // The label swap is CSS: .is-added hides the add label and shows "Shipped to parcel" with a check.
      window.clearTimeout(this.timer);
      this.button.classList.add('is-added');
      var self = this;
      this.timer = window.setTimeout(function () {
        self.button.classList.remove('is-added');
      }, 1600);
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }
  }

  /* <variant-picker> ---------------------------------------------------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.formId = this.getAttribute('data-form-id');
      this.addEventListener('change', this.onChange.bind(this));
      this.refreshAvailability();
    }

    selected() {
      return Array.prototype.map.call(this.querySelectorAll('fieldset.opt'), function (fs) {
        var checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    refreshAvailability() {
      var chosen = this.selected();
      var variants = this.variants;
      this.querySelectorAll('fieldset.opt').forEach(function (fs, index) {
        fs.querySelectorAll('input').forEach(function (input) {
          var trial = chosen.slice();
          trial[index] = input.value;
          var ok = variants.some(function (v) {
            return v.available && v.options.every(function (o, i) {
              return o === trial[i];
            });
          });
          var label = fs.querySelector('label[for="' + input.id + '"]');
          if (label) label.classList.toggle('is-unavailable', !ok);
        });
      });
    }

    onChange() {
      var chosen = this.selected();
      this.querySelectorAll('fieldset.opt').forEach(function (fs, i) {
        var out = fs.querySelector('[data-selected-value]');
        if (out && chosen[i]) out.textContent = chosen[i];
      });
      this.refreshAvailability();

      var variant = this.variants.find(function (v) {
        return v.options.every(function (o, i) {
          return o === chosen[i];
        });
      });
      this.applyVariant(variant);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant } }));
    }

    applyVariant(variant) {
      var scope = this.closest('[data-product-scope]') || document;
      var form = this.formId ? document.getElementById(this.formId) : null;

      if (form) {
        var idInput = form.querySelector('[data-variant-id]');
        var button = form.querySelector('[data-add-button]');
        if (idInput && variant) {
          idInput.value = variant.id;
          idInput.disabled = !variant.available;
        }
        if (button) {
          var label = button.querySelector('[data-add-label]');
          var price = button.querySelector('[data-add-price]');
          var ok = variant && variant.available;
          button.disabled = !ok;
          if (label) label.textContent = !variant ? strings.unavailable : variant.available ? strings.addToCart : strings.soldOut;
          if (price) {
            price.hidden = !ok;
            if (ok) price.textContent = ' · ' + variant.price;
          }
          button.classList.remove('is-added');
        }
      }

      if (!variant) return;

      setPrice(scope, variant.price, variant.compare);

      scope.querySelectorAll('[data-sku]').forEach(function (sku) {
        sku.textContent = variant.sku || '';
      });
      var title = scope.querySelector('[data-variant-title]');
      if (title) title.textContent = variant.options.join(' / ');

      if (this.getAttribute('data-update-url') === 'true' && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }

  /* <quantity-input> ---------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', this.onClick.bind(this));
      this.input.addEventListener('change', this.clamp.bind(this));
    }

    clamp() {
      var min = parseInt(this.input.min, 10);
      var value = parseInt(this.input.value, 10);
      if (isNaN(value) || value < (isNaN(min) ? 1 : min)) this.input.value = isNaN(min) ? 1 : min;
    }

    onClick(event) {
      var minus = event.target.closest('[data-qty-minus]');
      var plus = event.target.closest('[data-qty-plus]');
      if (!minus && !plus) return;
      event.preventDefault();
      var min = parseInt(this.input.min, 10);
      var floor = isNaN(min) ? 1 : min;
      var value = parseInt(this.input.value, 10) || floor;
      var next = Math.max(floor, value + (plus ? 1 : -1));
      if (next === value) return;
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* <theme-mode-toggle> -------------------------------------------------- */

  class ThemeModeToggle extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.buttons = this.querySelectorAll('[data-mode]');
      this.buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.set(btn.getAttribute('data-mode'));
        });
      });
      this.reflect(this.current());
    }

    current() {
      var stored = null;
      try {
        stored = localStorage.getItem('theme-mode');
      } catch (e) {}
      return stored || document.documentElement.getAttribute('data-default-scheme') || 'auto';
    }

    set(mode) {
      var root = document.documentElement;
      if (mode === 'auto') root.removeAttribute('data-theme');
      else root.setAttribute('data-theme', mode);
      try {
        localStorage.setItem('theme-mode', mode);
      } catch (e) {}
      this.reflect(mode);
    }

    reflect(mode) {
      this.buttons.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-mode') === mode ? 'true' : 'false');
      });
    }
  }

  /* <product-gallery> ---------------------------------------------------- */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.list = this.querySelector('[data-gallery-list]');
      this.items = this.querySelectorAll('.gallery__item');
      this.thumbs = this.querySelectorAll('.gallery__thumb');
      this.dots = this.querySelectorAll('.gallery__dot');
      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          self.select(thumb.getAttribute('data-media-id'));
        });
      });
      if (this.list) {
        this.list.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      }
      document.addEventListener('variant:change', function (event) {
        var scope = self.closest('[data-product-scope]');
        var variant = event.detail && event.detail.variant;
        if (variant && variant.media && scope && scope.contains(event.target)) {
          self.select(String(variant.media));
        }
      });
    }

    select(id) {
      var index = -1;
      this.items.forEach(function (item, i) {
        var match = item.getAttribute('data-media-id') === String(id);
        item.classList.toggle('is-active', match);
        if (match) index = i;
      });
      this.thumbs.forEach(function (thumb) {
        var match = thumb.getAttribute('data-media-id') === String(id);
        thumb.classList.toggle('is-active', match);
        thumb.setAttribute('aria-current', match ? 'true' : 'false');
      });
      this.setDot(index);
      if (index > -1 && this.list && this.list.scrollWidth > this.list.clientWidth + 1) {
        this.list.scrollTo({
          left: this.items[index].offsetLeft - this.list.offsetLeft,
          behavior: reduceMotion.matches ? 'auto' : 'smooth'
        });
      }
    }

    setDot(index) {
      this.dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    onScroll() {
      if (!this.items.length || !this.list.scrollWidth) return;
      var width = this.items[0].getBoundingClientRect().width + 8;
      var index = Math.round(this.list.scrollLeft / width);
      this.setDot(index);
    }
  }

  /* <product-recommendations> ------------------------------------------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      var self = this;
      if (!this.getAttribute('data-url') || this.querySelector('.grid')) return;
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      var observer = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) {
            observer.disconnect();
            self.load();
          }
        },
        { rootMargin: '300px' }
      );
      observer.observe(this);
    }

    load() {
      var self = this;
      fetch(this.getAttribute('data-url'))
        .then(function (res) {
          return res.text();
        })
        .then(function (text) {
          var next = parseHTML(text).querySelector('product-recommendations');
          if (next && next.innerHTML.trim()) self.innerHTML = next.innerHTML;
        })
        .catch(function () {});
    }
  }

  /* <collection-filters> ------------------------------------------------- */

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      var form = this.querySelector('form');
      if (!form) return;
      form.addEventListener('change', function (event) {
        var el = event.target;
        if (el.matches('.pill__input, .sort__input')) form.requestSubmit();
      });
    }
  }

  /* <legal-doc> -----------------------------------------------------------
     Builds the table of contents from the h2s inside the page body. Without JS the TOC stays
     hidden (the hidden attribute is in the markup) and the document reads on its own. On wide
     screens the TOC is a sticky, always-open field; below 990px it collapses above the body. */

  class LegalDoc extends HTMLElement {
    connectedCallback() {
      var toc = this.querySelector('[data-legal-toc]');
      var list = this.querySelector('[data-legal-toc-list]');
      var body = this.querySelector('[data-legal-body]');
      if (!toc || !list || !body) return;
      var headings = body.querySelectorAll('h2');
      if (!headings.length) return;
      var used = {};
      headings.forEach(function (h, i) {
        var base =
          (h.id ||
            h.textContent
              .toLowerCase()
              .trim()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '')) || 'section-' + (i + 1);
        var id = base;
        var n = 2;
        while (used[id] || (document.getElementById(id) && document.getElementById(id) !== h)) {
          id = base + '-' + n++;
        }
        used[id] = true;
        h.id = id;
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '#' + id;
        a.className = 'legal__toc-link';
        a.textContent = h.textContent.trim();
        li.appendChild(a);
        list.appendChild(li);
      });
      toc.hidden = false;
      var details = toc.querySelector('details');
      var wide = window.matchMedia('(min-width: 990px)');
      var sync = function () {
        details.open = wide.matches;
        details.classList.toggle('is-locked', wide.matches);
      };
      sync();
      if (wide.addEventListener) wide.addEventListener('change', sync);
      list.addEventListener('click', function (event) {
        if (!wide.matches && event.target.closest('a')) details.open = false;
      });
    }
  }

  /* <barcode-128> ---------------------------------------------------------
     Encodes data-value as Code 128 (set B) for the Libre Barcode 128 font:
     start B, the characters, a mod-103 check symbol and the stop pattern. */

  function code128(value) {
    var codes = [104];
    var sum = 104;
    for (var i = 0; i < value.length; i++) {
      var c = value.charCodeAt(i);
      if (c < 32 || c > 126) c = 45;
      var v = c - 32;
      codes.push(v);
      sum += v * (i + 1);
    }
    codes.push(sum % 103);
    var out = '';
    codes.forEach(function (v, index) {
      // Libre Barcode 128: value 0 (space) is U+00C2, 1-94 are ASCII 33-126, 95 and up are U+00C3 and up.
      var code = v === 0 ? 0xc2 : v < 95 ? v + 32 : v + 100;
      out += String.fromCharCode(index === 0 ? 0xcc : code);
    });
    return out + String.fromCharCode(0xce);
  }

  class Barcode128 extends HTMLElement {
    connectedCallback() {
      var value = this.getAttribute('data-value');
      if (value) this.textContent = code128(value);
    }
  }

  /* <tracking-story> -------------------------------------------------------
     Stages light up one by one (01 to 04) when the band scrolls into view, with the progress
     bar filling in step. Without JS, or with reduced motion, the server-rendered state stays. */

  class TrackingStory extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.stages = this.querySelectorAll('.stage');
      this.fill = this.querySelector('.track__fill');
      this.rider = this.querySelector('.track__rider');
      this.count = this.querySelector('[data-track-count]');
      this.total = this.stages.length;
      if (!this.total || reduceMotion.matches || !('IntersectionObserver' in window)) return;
      this.show(0);
      var observer = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) {
            observer.disconnect();
            self.run();
          }
        },
        { threshold: 0.35 }
      );
      observer.observe(this);
    }

    show(n) {
      this.stages.forEach(function (stage, i) {
        stage.classList.toggle('is-lit', i < n);
      });
      if (this.fill) this.fill.style.transform = 'scaleX(' + n / this.total + ')';
      if (this.rider) this.rider.style.setProperty('--p', n / this.total);
      if (this.count) {
        this.count.textContent = (strings.stagesProgress || '[[lit]] / [[total]] stages')
          .replace('[[lit]]', n)
          .replace('[[total]]', this.total);
      }
    }

    run() {
      var self = this;
      var n = 0;
      if (self.rider) self.rider.classList.add('is-walking');
      var timer = window.setInterval(function () {
        n += 1;
        self.show(n);
        if (n >= self.total) {
          window.clearInterval(timer);
          if (self.rider) window.setTimeout(function () { self.rider.classList.remove('is-walking'); }, 520);
        }
      }, 520);
    }
  }

  /* <manifest-tabs> ----------------------------------------------------------
     The tabs are plain links to the collection pages. With JS they swap the grid in place by
     fetching the collection URL with ?section_id= so the section re-renders for that collection. */

  class ManifestTabs extends HTMLElement {
    connectedCallback() {
      var self = this;
      var section = this.closest('.manifest-section');
      this.nav = section && section.querySelector('[data-manifest-tabs]');
      this.countEl = section && section.querySelector('[data-manifest-count]');
      if (!this.nav) return;
      this.nav.addEventListener('click', function (event) {
        var link = event.target.closest('[data-tab-url]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        self.load(link);
      });
    }

    load(link) {
      var self = this;
      var url = link.getAttribute('data-tab-url');
      var sectionId = this.getAttribute('data-section-id');
      var separator = url.indexOf('?') > -1 ? '&' : '?';
      if (this.controller) this.controller.abort();
      this.controller = 'AbortController' in window ? new AbortController() : null;
      this.setAttribute('aria-busy', 'true');
      fetch(url + separator + 'section_id=' + encodeURIComponent(sectionId), {
        signal: this.controller ? this.controller.signal : undefined
      })
        .then(function (res) {
          if (!res.ok) throw new Error('tab');
          return res.text();
        })
        .then(function (html) {
          var doc = parseHTML(html);
          var grid = doc.querySelector('[data-manifest-grid]');
          if (!grid) throw new Error('tab');
          self.querySelector('[data-manifest-grid]').innerHTML = grid.innerHTML;
          var count = doc.querySelector('[data-manifest-count]');
          if (count && self.countEl) self.countEl.textContent = count.textContent;
          self.nav.querySelectorAll('[data-tab-url]').forEach(function (tab) {
            var on = tab === link;
            tab.classList.toggle('is-active', on);
            if (on) tab.setAttribute('aria-current', 'true');
            else tab.removeAttribute('aria-current');
          });
          self.removeAttribute('aria-busy');
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          window.location.href = url;
        });
    }
  }

  /* Small behaviours ---------------------------------------------------- */

  // Login / recover panels.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-show-panel]');
    if (!btn) return;
    var wrap = btn.closest('[data-panels]');
    if (!wrap) return;
    var name = btn.getAttribute('data-show-panel');
    wrap.querySelectorAll('[data-panel]').forEach(function (panel) {
      panel.hidden = panel.getAttribute('data-panel') !== name;
    });
    var first = wrap.querySelector('[data-panel="' + name + '"] input');
    if (first) first.focus();
  });

  // Customer address deletion reuses the edit form with a DELETE method override.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-address-delete]');
    if (!btn) return;
    event.preventDefault();
    if (!window.confirm(btn.getAttribute('data-confirm'))) return;
    var form = btn.closest('form');
    var method = form.querySelector('input[name="_method"]');
    if (!method) {
      method = document.createElement('input');
      method.type = 'hidden';
      method.name = '_method';
      form.appendChild(method);
    }
    method.value = 'delete';
    form.submit();
  });

  document.addEventListener('DOMContentLoaded', function () {
    var recover = document.querySelector('[data-panel="recover"]');
    if (recover && recover.querySelector('.form-status')) {
      var wrap = recover.closest('[data-panels]');
      wrap.querySelectorAll('[data-panel]').forEach(function (panel) {
        panel.hidden = panel !== recover;
      });
    }
  });

  var elements = {
    'cart-drawer': CartDrawer,
    'cart-page': CartPage,
    'product-form': ProductForm,
    'variant-picker': VariantPicker,
    'quantity-input': QuantityInput,
    'theme-mode-toggle': ThemeModeToggle,
    'product-gallery': ProductGallery,
    'product-recommendations': ProductRecommendations,
    'collection-filters': CollectionFilters,
    'barcode-128': Barcode128,
    'tracking-story': TrackingStory,
    'manifest-tabs': ManifestTabs,
    'legal-doc': LegalDoc
  };
  Object.keys(elements).forEach(function (name) {
    if (!customElements.get(name)) customElements.define(name, elements[name]);
  });

  // Wire any drawer that is not inside <cart-drawer> (the mobile menu).
  document.querySelectorAll('dialog[data-drawer]').forEach(wireDialog);
})();
